// Generischer Client-Context-Store (PFS-330): app-keyed Key/Value + Pub/Sub.
//
// Haelt SELEKTION/Kontext (z.B. die aktive Praxis), NICHT gefetchte Remote-Daten
// (das ist der resourceStore — komplementaer, nicht ersetzt). Reine Infrastruktur,
// kein Domaenenwissen: der fachliche Owner (z.B. mednet-main) bestimmt die Form
// seines Contexts und setzt/liest die Werte.
//
// **Global-backed** (`globalThis`): in der Cut-App+Vendor-Shell-Architektur
// bundlet jede App ihre EIGENE @pfs/components-Kopie — eine modul-level Map waere
// pro Bundle getrennt. Das Global teilen alle Instanzen (siehe registerParamResolver).

export const PFS_CONTEXT_CHANGED_EVENT = "pfs:context-changed";

type ContextDict = Record<string, Record<string, string | null>>;

declare global {
  // Geteilter Store über ALLE @pfs/components-Bundle-Kopien (siehe Kopf-Kommentar).
  // eslint-disable-next-line no-var
  var __pfsContextStore: ContextDict | undefined;
}

function store(): ContextDict {
  return (globalThis.__pfsContextStore ??= {});
}

// Liest den aktuellen Context-Wert (`app`/`key`) oder null.
export function getContextValue(app: string, key: string): string | null {
  return store()[app]?.[key] ?? null;
}

// Setzt den Context-Wert + broadcastet `pfs:context-changed` auf document.body,
// damit Consumer (WCs ueber ihre injizierte Source-Funktion) neu lesen. Idempotent:
// gleicher Wert = no-op (kein Echo).
export function setContextValue(app: string, key: string, value: string | null): void {
  const s = store();
  const bucket = (s[app] ??= {});
  if (bucket[key] === value) return;
  bucket[key] = value;
  if (typeof document !== "undefined") {
    document.body?.dispatchEvent(
      new CustomEvent(PFS_CONTEXT_CHANGED_EVENT, { detail: { app, key, value } }),
    );
  }
}

// --- Source-Injektions-Protokoll -------------------------------------------
// Ein DUMMES, umgebungs-agnostisches WC kennt seine Umgebung nicht (mednet-main /
// WP-Embed / standalone). Es fragt seinen Host nach einer Wert-Quelle BY NAME;
// der Host (z.B. mednet-main) liefert eine (ggf. async) Funktion. Das WC ruft nur
// "gib mir X" und kennt die Herkunft nicht.
//
// Mechanik: composed CustomEvent — crosst die pfs-shell-Shadow-Grenze (anders als
// DOM-Traversal/Property-Injektion) und bubbelt bis document, wo der Host hoert.

export const PFS_REQUEST_SOURCE_EVENT = "pfs:request-source";

// Eine vom Host bereitgestellte, benannte Funktion ("Port") — der NAME definiert
// den Vertrag: eine SOURCE (Read, `() => value`) ODER ein SINK (Write/Callback,
// `(value) => void`). Ein einziger Mechanismus fuer beide Richtungen, damit das
// WC NIE ein globales Event braucht — es ruft nur seine injizierten Ports.
// (...args: never[]) = „irgendeine Funktion" OHNE any — jede konkrete Signatur
// (Source () => …, Sink (v) => void) ist dazu zuweisbar (Top-Function-Typ).
export type HostFn = (...args: never[]) => unknown;
// Bequemlichkeits-Alias fuer den haeufigsten Fall (Read-Source).
export type ValueSource = () => string | null | undefined | Promise<string | null | undefined>;

// Vom WC gerufen: fragt den Host nach dem Port `name`. Der Host antwortet synchron
// via detail.provide(fn). Liefert die Funktion oder null (kein Host → standalone/
// Host-noch-nicht-bereit; das WC degradiert grazioes). Generisch ueber den
// Funktionstyp — Source (Default) oder Sink: requestSource<(id:string)=>void>(...).
export function requestSource<T extends HostFn = ValueSource>(el: Element, name: string): T | null {
  let provided: T | null = null;
  el.dispatchEvent(
    new CustomEvent(PFS_REQUEST_SOURCE_EVENT, {
      bubbles: true,
      composed: true,
      detail: {
        name,
        provide: (fn: T) => {
          provided = fn;
        },
      },
    }),
  );
  return provided;
}

// Vom Host (z.B. mednet-main) gerufen: registriert eine Port-Funktion fuer `name`
// (Source ODER Sink). Antwortet auf jedes WC-`requestSource(name)`. Liefert einen
// Disposer, der die Registrierung wieder entfernt (Host kann eine Quelle abmelden/
// ersetzen; Test-Isolation).
export function provideSource<T extends HostFn>(name: string, fn: T): () => void {
  if (typeof document === "undefined") return () => {};
  const listener = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { name: string; provide: (fn: T) => void };
    if (detail?.name === name) detail.provide(fn);
  };
  document.addEventListener(PFS_REQUEST_SOURCE_EVENT, listener);
  return () => document.removeEventListener(PFS_REQUEST_SOURCE_EVENT, listener);
}

// readSource: One-Shot-Komfort — fragt den Host nach `name`, ruft die (ggf. async)
// Funktion und liefert den Wert (oder `fallback`, wenn kein Host/kein Wert). Rein
// generisch (Name = Parameter, kein Domaenenwissen). Der NAMEN-Katalog (apiBase,
// practiceId, …) gehoert dem Konsumenten/der Domaene, NICHT pfs-components.
export async function readSource(
  el: Element,
  name: string,
  fallback: string | null = null,
): Promise<string | null> {
  const src = requestSource<ValueSource>(el, name);
  if (!src) return fallback;
  try {
    const v = await src();
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

// readSourceSync: synchroner One-Shot fuer statisch-synchrone Werte, die element-
// LOS gebraucht werden (z.B. apiBase in apiFetch/banner-bus — Modul-Code ohne
// Element + sync URL-Bau). Dispatcht vom `document.body` (crosst die Shadow-Grenze
// genauso) und ruft die Host-Quelle synchron. Liefert nur Strings; eine async/
// non-string-Quelle → `fallback` (fuer async `readSource` nutzen). Wenn KEIN Host
// `name` bereitstellt (standalone) → `fallback` — graziose Degradation.
export function readSourceSync(name: string, fallback = ""): string {
  if (typeof document === "undefined" || !document.body) return fallback;
  const src = requestSource<() => unknown>(document.body, name);
  if (!src) return fallback;
  try {
    const v = src();
    return typeof v === "string" ? v : fallback;
  } catch {
    return fallback;
  }
}
